import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import AddMemberToQuote from '@components/assign-team-member/AddMemberToQuote';
import SampleComment from '@components/SampleComment';
import { toastError, toastSuccess } from '@components/Toast';
import TopDetails from '@components/TopDetails';

import { clearCurrentActivePage, setCurrentActivePage } from '@store/actions/ActivePageAction';
import { fetchCostingDetails } from '@store/actions/CostingAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { onUpdateEOD } from '@services/quoteServices';

import ApiClient from '@lib/apiClient';

import {
	changeDateFormat,
	getTodaysDate,
	getUserInfo,
	packTag,
	renderSetImages,
	setTag,
	STATIC_SET_CONSTANTS
} from '@utils/Util';

const QuoteRequestDetails = () => {
	const [costingDetails, setCostingDetails] = useState(null);
	const [showComment, setShowComment] = useState(false);
	const [selectedId, setSelectedId] = useState(null);
	const [idsForChangeDate, setIdsForChangeDate] = useState([]);
	const costingStore = useSelector((store) => store.costingStore);
	const costingItem = costingDetails?.quoteItemResponseList;
	const [isApproveBtnDisabled, setIsApproveBtnDisabled] = useState(false);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { id } = useParams();

	useEffect(() => {
		if (costingDetails?.quoteApprovalStatus === 'APPROVED') {
			navigate(`/costing/${costingDetails?.id}`, { replace: true });
		}
	}, [costingDetails?.quoteApprovalStatus]);

	useEffect(() => {
		setCostingDetails(costingStore.costingDetails);
	}, [costingStore]);

	useEffect(() => {
		dispatch(openLoader());
		dispatch(fetchCostingDetails(id)).finally(() => {
			dispatch(closeLoader());
		});
		return () => {
			dispatch(clearCurrentActivePage());
		};
	}, []);

	const onClearChat = () => {
		setCostingDetails((prevQuoteStore) => ({
			...prevQuoteStore,
			quoteItemResponseList: prevQuoteStore?.quoteItemResponseList?.map((item) => {
				if (item.productId === selectedId) {
					return {
						...item,
						noOfUnreadMessage: 0
					};
				}
				return item;
			})
		}));
		setSelectedId(null);
	};

	const handleCommentClose = () => {
		onClearChat();
		setShowComment(false);
		dispatch(clearCurrentActivePage());
	};

	const onApprove = async () => {
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.POST('quoteRequest', {}, `${id}/approve`);
			dispatch(closeLoader());
			if (data) {
				navigate(`/costing/${id}`, { replace: true });
			}
		} catch ({ response }) {
			dispatch(closeLoader());
			if (response && response.data && response.data.message) {
				toastError(response.data.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		}
	};

	const onCancel = async () => {
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.POST('quoteRequest', {}, `${id}/cancel`);
			dispatch(closeLoader());
			if (data) {
				navigate('/buyer-requests');
			}
		} catch ({ response }) {
			dispatch(closeLoader());
			if (response && response.data && response.data.message) {
				toastError(response.data.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		}
	};

	const getStatus = (status) => {
		if (status === 'APPROVED') return 'green';
		// if (status === 'REQUEST_FOR_REVISION') return 'green';
		return 'secondary';
	};

	const isProductHasSet = (product) => {
		return product.isSet || product.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT;
	};

	const isProductHasPack = (product) => {
		return product.isPack || product.setPackStyleFlag === STATIC_SET_CONSTANTS.PACK_PRODUCT;
	};

	const getOfferPrice = (item) => (
		<p className={`regular-14 ${getStatus(item.status)} weight-500`}>
			{item?.adminOfferPrice && (
				<span className="d-block">
					${parseFloat(item?.adminOfferPrice).toFixed(2)}
					{item?.margin && <span className="pl-1 gray_dark_01">({item?.margin}%)</span>}
				</span>
			)}
			{item?.priceUpdateDTOArray &&
				item?.priceUpdateDTOArray?.map((price, idx) => (
					<p className="regular-12 gray_dark_02 weight-500" key={`price_${idx}`}>
						<del>${parseFloat(price?.previousValue).toFixed(2)}</del>
					</p>
				))}
		</p>
	);

	const handleDateChange = (value, productid) => {
		setCostingDetails((prevCostingDetails) => {
			const updatedQuoteItemResponseList = prevCostingDetails?.quoteItemResponseList?.map((item) => {
				if (item.expectedOrderDate && item.productId === productid) {
					item.expectedOrderDate = value;
					setIdsForChangeDate((prevIds) => [...new Set([...prevIds, item.productId])]);
				}

				if (!item.expectedOrderDate) {
					item.expectedOrderDate = value;
					setIdsForChangeDate((prevIds) => [...new Set([...prevIds, item.productId])]);
				}

				return item;
			});

			return {
				...prevCostingDetails,
				quoteItemResponseList: updatedQuoteItemResponseList
			};
		});
	};

	const onChangeDate = async (value) => {
		try {
			dispatch(openLoader());
			const response = await onUpdateEOD(id, idsForChangeDate, value); // Make the API call
			toastSuccess(response?.message);
		} finally {
			dispatch(closeLoader());
			setIdsForChangeDate([]);
		}
	};

	return (
		<div>
			<div className="top-header bg-white">
				<div className="filter-container-top pt-3 pb-3 bg-gray">
					<div className="filters">
						<div className="sample-full-status">
							<ul className="common-list-component mb-2">
								<li>
									<img
										className="cursor-pointer"
										src={`${process.env.PUBLIC_URL}/icons/back-arrow-gray.svg`}
										alt="back"
										onClick={() => navigate(-1)}
									/>
									<span className="gray_dark_01 ml-2">
										{costingDetails?.title || costingDetails?.collectionName}
									</span>
								</li>
								<li>
									<span>{costingDetails?.referenceNumber} </span>
								</li>
								<li>
									<span>{costingDetails?.requestedBy}</span>
								</li>
								<li>
									<span>
										{changeDateFormat(costingDetails?.requestedDate, '', 'Do MMM')}
									</span>
								</li>
								<li>
									<span>{costingDetails?.noOfDesign} Design</span>
								</li>
							</ul>
						</div>
					</div>
					<div className="d-flex align-items-center">
						<AddMemberToQuote
							quoteId={costingDetails?.id}
							toggleBtnDisabled={setIsApproveBtnDisabled}
							requestFrom="PENDING_REQUEST"
						/>

						{costingDetails?.quoteApprovalStatus === 'PENDING' && (
							<div className="ml-3">
								<button
									className="button gray-outline size32 mr-3"
									onClick={() => onCancel()}
								>
									<span>Cancel</span>
								</button>
								<button
									disabled={isApproveBtnDisabled}
									className="button approve size32"
									onClick={() => onApprove()}
								>
									<span>Approve</span>
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
			<div className="sample-request-gorup mt-0 px-4">
				<div className="costing-dashboard-container bg-gray">
					<TopDetails
						type={'QUOTE'}
						description={costingDetails?.description}
						documentLists={costingDetails?.requestedDocumentList}
					/>
					<div className="scroll-x-label">
						<table className="table with-border">
							<thead>
								<tr>
									<th>Design({costingDetails?.quoteItemResponseList?.length})</th>
									<th>Description</th>
									<th>Qty</th>
									<th>Target</th>
									<th>Production price</th>
									{getUserInfo().primaryUserType !== 'COSTING_MANAGER' && (
										<th>Offer price</th>
									)}
									<th>Expected Order Date</th>
									<th>Remarks</th>
									<th>Conversation</th>
								</tr>
							</thead>
							<tbody>
								{costingItem?.map((item, idx) => (
									<tr key={`${item.quantityWiseCostingId}_${idx}`}>
										<td
											className={`${item.isSameProduct && 'd-none'}`}
											rowSpan={item?.noOfSameProduct}
										>
											<div className="d-flex">
												<div className="design-image">
													{isProductHasSet(item) ? (
														renderSetImages(item?.setMemberList, '56px')
													) : (
														<img
															src={item?.featureImageDocUrl}
															alt="image"
															className="cell-img pr-2"
														/>
													)}
												</div>
												<div className="ml-1">
													<div className="mb-1">
														{isProductHasSet(item) && setTag()}
														{isProductHasPack(item) && packTag()}
													</div>
													{item?.productRefNo} <br />
													<ul className="common-list-component mb-2">
														<li>
															{isProductHasSet(item)
																? item?.setMemberList[0]?.market
																: item?.market}
														</li>
														{isProductHasSet(item) && item?.setMemberList ? (
															item?.setMemberList.map((member) => (
																<li key={member?.id}>{member?.category} </li>
															))
														) : (
															<li>{item?.category} </li>
														)}
													</ul>
												</div>
											</div>
										</td>
										<td
											className={`cursor-pointer ${item.isSameVariant && 'd-none'}`}
											rowSpan={item?.noOfSameVariant}
										>
											{isProductHasSet(item) && item?.setMemberList ? (
												<ul className="with-bullet">
													{item?.setMemberList.map((member) => (
														<li key={member?.id}>{member?.variation}</li>
													))}
												</ul>
											) : (
												<span className="cursor-pointer">
													{item?.variation || ''}
												</span>
											)}
										</td>
										<td
											className="cursor-pointer"
											data-toggle="modal"
											data-target="#designViewModal"
										>
											<span className="regular-14 gray_dark_01">{item?.quantity}</span>
										</td>
										<td>
											<span className="regular-14 gray_dark_01">{item?.target}</span>
										</td>
										<td
											className="cursor-pointer"
											data-toggle="modal"
											data-target="#designViewModal"
										>
											<span className="regular-14 gray_dark_01">{item?.price}</span>
										</td>
										{getUserInfo().primaryUserType !== 'COSTING_MANAGER' && (
											<td>{getOfferPrice(item)}</td>
										)}
										<td
											className={`${item.isSameProduct && 'd-none'}`}
											rowSpan={item?.noOfSameProduct}
										>
											<input
												type="date"
												name="eoDate"
												min={getTodaysDate()}
												value={item?.expectedOrderDate}
												onChange={(e) =>
													handleDateChange(e.target.value, item?.productId)
												}
												onBlur={(e) => onChangeDate(e.target.value, item?.productId)}
											/>
										</td>
										<td>
											<span className="regular-14 gray_dark_02">{item?.remarks}</span>
										</td>
										<td>
											<div className="conversation-btn">
												<button
													className="button text"
													onClick={() => {
														dispatch(
															setCurrentActivePage({
																id: item?.productId,
																type: 'PRODUCT_CONVERSATION',
																pageType: 'DETAILS'
															})
														);
														setSelectedId(item?.productId);
														setShowComment(true);
													}}
												>
													{item?.noOfUnreadMessage > 0 ? (
														<img
															src={`${process.env.PUBLIC_URL}/icons/comment-not-read.svg`}
															alt="not read"
														/>
													) : (
														<img
															src={`${process.env.PUBLIC_URL}/icons/comments-gray.svg`}
															alt="not read"
														/>
													)}
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<div className="col-md-1 one-sixth text-right">
				{selectedId && (
					<SampleComment
						sampleRequestId={costingDetails?.id || id}
						postTypeFilter={'PRODUCT_DEVELOPMENT_COMMENT'}
						topic={'PRODUCT_QUOTE_COMMENT'}
						postTypeId={'quoteRequestId'}
						productId={selectedId}
						showComment={showComment}
						setShowComment={handleCommentClose}
						brandInfo={{
							brandId: costingDetails.brandId,
							brandName: costingDetails.brandName
						}}
					/>
				)}
			</div>
		</div>
	);
};

export default QuoteRequestDetails;
